import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  ComposedChart,
  Line,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { Box, Stack, Typography } from '@mui/material';

export default function AnnounceTrendGraph({ trendData, height = '85%' }) {
  const cleaned = useMemo(() => {
    if (!Array.isArray(trendData)) return [];

    return trendData
      .map((d) => {
        const ts = d.date ? new Date(d.date).getTime() : NaN;

        return {
          timestamp: ts,
          announced: Number(d.announcedCount ?? 0),
          notAnnounced: Number(d.notAnnouncedCount ?? 0)
        };
      })
      .filter((d) => Number.isFinite(d.timestamp))
      .sort((a, b) => a.timestamp - b.timestamp);
  }, [trendData]);

  const formatDate = (ts) => {
    const dt = new Date(ts);
    return dt.toLocaleDateString('en-IN', {
      month: 'short',
      day: 'numeric'
    });
  };

  if (!cleaned.length) {
    return (
      <div
        style={{
          height,
          textAlign: 'center',
          paddingTop: 20,
          color: 'gray',
          fontWeight: 600,
          fontSize: 12
        }}
      >
        No trend data available
      </div>
    );
  }

  const rows = [
    {
      name: 'Announced',
      color: '#B2F855'
    },
    {
      name: 'Not Announced',
      color: '#FF9263'
    }
  ];

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;

    const dt = new Date(label);
    const dateLabel = dt.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short'
    });

    const rows = [
      {
        name: 'Announced',
        value: payload[0]?.payload?.announced ?? 0,
        color: '#B2F855'
      },
      {
        name: 'Not Announced',
        value: payload[0]?.payload?.notAnnounced ?? 0,
        color: '#FF9263'
      }
    ];

    return (
      <div
        style={{
          background: '#fff',
          borderRadius: 12,
          padding: '8px',
          boxShadow: '0px 2px 8px rgba(0,0,0,0.15)',
          minWidth: 160
        }}
      >
        <div
          style={{
            fontWeight: 700,
            fontSize: 14,
            color: '#171D40',
            marginBottom: 10
          }}
        >
          {dateLabel}
        </div>

        {rows.map((row) => (
          <div
            key={row.name}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 8,
              gap: 12
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  background: row.color
                }}
              />
              <span
                style={{
                  color: '#171D40',
                  fontSize: 13,
                  fontWeight: 700
                }}
              >
                {row.name}
              </span>
            </div>

            <span
              style={{
                color: '#171D40',
                fontSize: 13,
                fontWeight: 800,
                paddingLeft: 8
              }}
            >
              {row.value}
            </span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div style={{ width: '100%', height }}>
      <ResponsiveContainer>
        <ComposedChart data={cleaned} margin={{ top: 20, right: 30, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id="announcedGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#B2F855" />
              <stop offset="100%" stopColor="#FFFFFF" />
            </linearGradient>

            <linearGradient id="notAnnouncedGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#FF9263" />
              <stop offset="100%" stopColor="#FFFFFF"  />
            </linearGradient>
          </defs>

          <CartesianGrid stroke="#eee" vertical={false} />

          <XAxis
            dataKey="timestamp"
            tickFormatter={formatDate}
            tick={{ fill: '#98A1B3', fontSize: 12 }}
            axisLine={false}
            tickLine={false}
            interval={0}
            minTickGap={0}
          />

          <YAxis tick={{ fill: '#98A1B3', fontSize: 12 }} axisLine={false} tickLine={false} />

          <Tooltip content={<CustomTooltip />} cursor={{ stroke: '#ccc', strokeWidth: 1 }} />

          <Area
            type="monotone"
            dataKey="announced"
            stroke="#9BE04E"
            fill="url(#announcedGrad)"
            strokeWidth={2}
            dot={false}
            legendType="none"
            tooltipType="none"
          />

          <Area
            type="monotone"
            dataKey="notAnnounced"
            stroke="#FF9263"
            fill="url(#notAnnouncedGrad)"
            strokeWidth={2}
            dot={false}
            legendType="none"
            tooltipType="none"
          />

          <Line
            type="monotone"
            dataKey="announced"
            name="Announced"
            stroke="#9BE04E"
            strokeWidth={2}
            dot={false}
          />

          <Line
            type="monotone"
            dataKey="notAnnounced"
            name="Not Announced"
            stroke="#FF9263"
            strokeWidth={2}
            dot={false}
          />
        </ComposedChart>
      </ResponsiveContainer>

      {/* Legend */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          mt: 1,
          flexWrap: 'wrap',
          justifyContent: 'center'
        }}
      >
        {rows.map((item, i) => (
          <Box
            key={i}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 0.8,
              py: 0.5,
              borderRadius: '5px',
              backgroundColor: '#FFFFFF',
              boxShadow: '0px 4px 12px rgba(0,0,0,0.08)'
            }}
          >
            <svg width="5" height="5" viewBox="0 0 8 8">
              <circle cx="4" cy="4" r="4" fill={item.color} />
            </svg>
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 700,
                color: '#171D40',
                whiteSpace: 'nowrap'
              }}
            >
              {item.name}
            </Typography>
          </Box>
        ))}
      </Box>
    </div>
  );
}

AnnounceTrendGraph.propTypes = {
  trendData: PropTypes.array.isRequired,
  height: PropTypes.number
};
